import {
  useState,
  useEffect,
  useRef,
  lazy,
  Suspense,
  type ReactNode,
} from "react";
import { useParams, Link } from "react-router";
import { useQuery, useMutation } from "convex/react";
import { api } from "@todotogether/backend/_generated/api";
import type { Id } from "@todotogether/backend/_generated/dataModel";
import {
  GENERIC_ERROR_MESSAGE,
  ITEM_TITLE_MAX,
  PAID_EXPORT_MESSAGE,
  PRESENCE_TEASE_MESSAGE,
  attributionColor,
  canChangeMemberRole,
  capitaliseFirstLetter,
  dueDateToMs,
  dueInfo,
  dueMsToDate,
  errorMessage,
  memberLabel,
  orderKeyAfter,
  presenceAddingLabel,
  presenceAnnounceEditing,
  presenceTypingLabel,
  SEE_PLANS_LINK_LABEL,
  resolveExportTap,
  roleAtLeast,
  roleOptionsFor,
  selectedInviteRole,
  starredFirst,
  typistsOnItem,
  type InviteRoleChoice,
  type PresencePayloadRow,
  type Role,
} from "@todotogether/shared";
import {
  useListTypingPresence,
  useTypingBroadcaster,
} from "./typingPresence";
import {
  useCalendarExport,
  type ExportRequest,
} from "./useCalendarExport";

// Ticket #13: BlockNote + Mantine are a heavy bundle — loaded only when a
// Note is actually opened, never as part of the list-screen payload.
const NotesEditor = lazy(() => import("./Notes"));
type NotesDoc = Array<Record<string, unknown>>;

/**
 * One List (ticket #11): add Items fast, tick done, inline title edits,
 * reorder (fractional keys, server-side), Assignee picker over the List's
 * Members, done Items collapsed into a bottom section. Item rows keep
 * their editing chrome behind a per-row menu (#195) — the default row is
 * just checkbox, title, due badge, assignee chip, the mobile redesign's
 * quiet shape.
 */
export default function ListDetail() {
  const { listId } = useParams<{ listId: string }>();
  const list = useQuery(
    api.lists.getList,
    listId ? { listId: listId as Id<"lists"> } : "skip",
  );
  const items = useQuery(
    api.items.listItems,
    listId ? { listId: listId as Id<"lists"> } : "skip",
  );
  const members = useQuery(
    api.lists.getMembers,
    listId ? { listId: listId as Id<"lists"> } : "skip",
  );
  // Live typing presence (#386, the #385 vocabulary ported): the read
  // rides the page's own lifecycle — web pages unmount with the route,
  // so the query's teardown IS the screen-exit teardown. Which arm
  // renders derives from the payload's shape (typists vs
  // latestActivityAt), never a local tier read — the mirrored-gate
  // law; a mid-lapse tier flip re-shapes the wire and the UI follows.
  const { typists, adders, teaseLive } = useListTypingPresence(listId);
  const { noteTyping, stopTyping } = useTypingBroadcaster(
    listId as Id<"lists">,
  );
  // ONE polite status region per page (#385's a11y law, shared):
  // typing STARTS are announced once per typist — stops are chatter.
  const [announcement, setAnnouncement] = useState("");
  const announcedTypists = useRef<Set<string>>(new Set());
  const typistsRef = useRef(typists);
  typistsRef.current = typists;
  const typistKey = typists.map((typist) => typist.userId).join(" ");
  useEffect(() => {
    const started = typistsRef.current.filter(
      (typist) => !announcedTypists.current.has(typist.userId),
    );
    announcedTypists.current = new Set(
      typistsRef.current.map((typist) => typist.userId),
    );
    if (started.length === 0) return;
    setAnnouncement(
      started
        .map((typist) => presenceAnnounceEditing(typist.name))
        .join(". "),
    );
  }, [typistKey]);

  // Server rejections surface here (optimistic updates roll back
  // automatically — Convex does that — the message is ours to show).
  const [mutationError, setMutationError] = useState<string | null>(null);
  const onError = (err: unknown) => {
    setMutationError(errorMessage(err, GENERIC_ERROR_MESSAGE));
  };
  // View controls: slice a busy shared list. Default "all" keeps the
  // original open/done split byte-identical.
  const [filter, setFilter] = useState<"all" | "open" | "done" | "mine">("all");
  // Recovery shouldn't leave a stale rejection banner on screen forever.
  useEffect(() => {
    if (mutationError === null) return;
    const t = setTimeout(() => setMutationError(null), 6000);
    return () => clearTimeout(t);
  }, [mutationError]);

  // Export-to-calendar tier awareness (#375): the #374 mobile
  // convention — the client holds the tap decision (resolveExportTap;
  // an unresolved entitlement reads free, ADR-0009's fail-safe) and
  // the server re-refuses an unpaid caller regardless. This read
  // belongs to the export entries alone; the presence arm above still
  // derives from the wire's shape (the mirrored-gate law, untouched).
  const entitlement = useQuery(api.entitlements.getMine);
  const viewerIsPaid = entitlement?.tier === "paid";
  // The export status slot: the free tap's upsell is an affordance and
  // stays; the paid path's refusals (empty list, undated item) clear
  // themselves like every rejection banner.
  const [exportStatus, setExportStatus] = useState<
    { kind: "upsell" } | { kind: "error"; message: string } | null
  >(null);
  const { requestExport } = useCalendarExport({
    onError: (message) => setExportStatus({ kind: "error", message }),
  });
  // The one funnel for both entries — the free arm returns before the
  // seam is ever armed; the paid arm clears any stale status and arms.
  const startExport = (request: ExportRequest) => {
    if (resolveExportTap(viewerIsPaid) === "upsell") {
      setExportStatus({ kind: "upsell" });
      return;
    }
    setExportStatus(null);
    requestExport(request);
  };
  useEffect(() => {
    if (exportStatus?.kind !== "error") return;
    const t = setTimeout(() => setExportStatus(null), 6000);
    return () => clearTimeout(t);
  }, [exportStatus]);

  if (list === undefined || items === undefined) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-paper">
        <p className="text-muted" data-testid="list-loading">
          Loading…
        </p>
      </main>
    );
  }

  // Non-Members and soft-deleted Lists see this (server returns null).
  if (list === null || items === null) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-paper">
        <div className="text-center">
          <p className="text-muted" data-testid="list-unavailable">
            This list doesn't exist or you don't have access to it.
          </p>
          <BackToLists />
        </div>
      </main>
    );
  }

  // List-level capabilities derive through the shared role home (#820)
  // — the same calls mobile's ListDetailScreen makes — not hand-rolled
  // string ladders; the shared rank fails closed on a non-Role string.
  const canEdit = roleAtLeast(list.role, "editor");
  const canShare = roleAtLeast(list.role, "admin");

  // "Mine" keeps the open/done split but narrows to the viewer's own
  // assignments (unassigned rows are nobody's).
  const mine = (i: Item) =>
    list.viewerMemberId !== undefined &&
    i.assigneeMemberId === list.viewerMemberId;
  // Starred open Items pin above unstarred ones (#248): the SHARED
  // starredFirst stable partition over the caller's own stamps (the
  // server already returns them, #247 — the Lists screen's own seam,
  // #70), each group keeping its manual order, so unstarring restores
  // exactly the prior arrangement. The done section is never
  // partitioned: a star survives completion, but it does not reorder
  // history. "Mine" filters the partitioned document — the visible
  // order is what the row actions operate on.
  const allOpen = starredFirst(items.open);
  const visibleOpen = filter === "mine" ? allOpen.filter(mine) : allOpen;
  const visibleDone = filter === "mine" ? items.done.filter(mine) : items.done;
  // Mine with nothing of yours in EITHER section — open or done. Gating
  // on the open rows alone rendered "Nothing assigned to you." straight
  // above your own done rows (and went silent on a fully swept list).
  const mineEmpty =
    filter === "mine" &&
    visibleOpen.length === 0 &&
    visibleDone.length === 0;

  return (
    <main className="min-h-screen bg-paper px-6 py-10">
      {/* The ONE polite live region (#385's law, shared): the sr-only
          status region announces typing STARTS only. */}
      <div role="status" aria-live="polite" className="sr-only">
        {announcement}
      </div>
      <div className="mx-auto max-w-2xl">
        <BackToLists />
        <header className="mt-4 flex items-center justify-between">
          <h1
            className="font-serif text-[28px] font-bold leading-[34px] text-ink"
            data-testid="list-title"
          >
            {list.name}
          </h1>
          <div className="flex shrink-0 items-center gap-3">
            <button
              type="button"
              data-testid={`export-list-${list.name}`}
              onClick={() => startExport({ kind: "list", listId: list._id })}
              className="text-sm text-muted underline hover:text-ink"
            >
              Export to calendar
            </button>
            <span className="text-sm text-muted" data-testid="viewer-role">
              {list.role}
            </span>
          </div>
        </header>

        {/* The export status slot (#375): the free tap's upsell (the
            paywall at the bite — the notes-paywall house link) and the
            paid path's refusals, inline under the header both entries
            drive. */}
        {exportStatus?.kind === "upsell" && (
          <p className="mt-3 text-sm text-muted" data-testid="export-upsell">
            {PAID_EXPORT_MESSAGE}{" "}
            <Link
              to="/app/settings"
              data-testid="export-upsell-link"
              className="underline underline-offset-2"
            >
              {SEE_PLANS_LINK_LABEL}
            </Link>
          </p>
        )}
        {exportStatus?.kind === "error" && (
          <p
            className="mt-3 rounded-lg border border-danger/40 bg-page px-3 py-2 text-sm text-danger"
            data-testid="export-error"
          >
            {exportStatus.message}
          </p>
        )}

        {/* The free-tier tease (#386 = #385's pill): the ONLY presence
            element a free subscriber sees, activity-driven (never a
            static nag), NEUTRAL dot — no member colour can reach the
            free experience. The whole pill is the >=44px target and
            lands on the settings/plans route — Settings stays the one
            paywall surface. */}
        {teaseLive && (
          <Link
            to="/app/settings"
            data-testid="presence-tease"
            className="mt-3 flex min-h-[44px] items-center gap-2 rounded-xl border border-line bg-page px-4 py-2 text-sm font-medium text-ink no-underline shadow-sm hover:bg-pressed"
          >
            <span
              aria-hidden="true"
              className="h-2 w-2 shrink-0 rounded-full bg-faint"
            />
            {PRESENCE_TEASE_MESSAGE}
          </Link>
        )}

        {mutationError && (
          <p
            className="mt-3 rounded-lg border border-danger/40 bg-page px-3 py-2 text-sm text-danger"
            data-testid="items-error"
          >
            {mutationError}
          </p>
        )}

        {canEdit ? (
          <AddItem
            listId={list._id as Id<"lists">}
            onError={onError}
            onTyping={() => noteTyping("composer")}
            onStopTyping={stopTyping}
          />
        ) : (
          <p className="mt-6 text-sm text-muted" data-testid="viewer-readonly">
            You have view-only access to this list.
          </p>
        )}

        <FilterBar
          filter={filter}
          setFilter={setFilter}
          doneCount={items.done.length}
          canEdit={canEdit}
          listId={list._id as Id<"lists">}
          onError={onError}
        />

        {filter !== "done" && !mineEmpty && (
          <OpenItems
            items={visibleOpen}
            members={members ?? []}
            canEdit={canEdit}
            viewerMemberId={list.viewerMemberId}
            onError={onError}
            typists={typists}
            adders={adders}
            onTypingDraft={(itemId) => noteTyping(`item:${itemId}`, itemId)}
            onStopTyping={stopTyping}
            onExportItem={(itemId) => startExport({ kind: "item", itemId })}
          />
        )}
        {filter === "done" && items.done.length === 0 && (
          <p
            className="mt-6 rounded-xl border border-dashed border-line bg-page p-6 text-center text-sm text-muted"
            data-testid="no-done-items"
          >
            Nothing completed yet.
          </p>
        )}
        {mineEmpty && (
          <p
            className="mt-6 rounded-xl border border-dashed border-line bg-page p-6 text-center text-sm text-muted"
            data-testid="no-mine-items"
          >
            Nothing assigned to you.
          </p>
        )}
        {filter !== "open" && (
          <DoneItems
            items={visibleDone}
            members={members ?? []}
            canEdit={canEdit}
            viewerMemberId={list.viewerMemberId}
            onError={onError}
            typists={typists}
            onTypingDraft={(itemId) => noteTyping(`item:${itemId}`, itemId)}
            onStopTyping={stopTyping}
            onExportItem={(itemId) => startExport({ kind: "item", itemId })}
          />
        )}

        {canShare && <SharePanel listId={list._id as Id<"lists">} />}
        <MembersPanel
          listId={list._id as Id<"lists">}
          members={members ?? []}
          callerRole={list.role}
          myMemberId={list.viewerMemberId as Id<"listMembers">}
        />
      </div>
    </main>
  );
}

function BackToLists() {
  return (
    <Link to="/app" className="text-sm text-muted hover:underline" data-testid="back-to-lists">
      ← Your lists
    </Link>
  );
}

type ListFilter = "all" | "open" | "done" | "mine";

/**
 * View controls for a busy shared list: the All/Open/Done/Mine filter
 * chips, and the confirm-gated "clear completed" sweep (bulk-delete of
 * done Items — the Done section is history, not a second inbox).
 * "Clear completed" is an Editor affordance; the filter is for everyone.
 */
function FilterBar({
  filter,
  setFilter,
  doneCount,
  canEdit,
  listId,
  onError,
}: {
  filter: ListFilter;
  setFilter: (f: ListFilter) => void;
  doneCount: number;
  canEdit: boolean;
  listId: Id<"lists">;
  onError: (err: unknown) => void;
}) {
  const clearCompleted = useMutation(api.items.clearCompleted);
  const [confirming, setConfirming] = useState(false);

  const chips: Array<{ id: ListFilter; label: string }> = [
    { id: "all", label: "All" },
    { id: "open", label: "Open" },
    { id: "done", label: "Done" },
    { id: "mine", label: "Mine" },
  ];

  return (
    <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="filter-bar">
      <div className="flex overflow-hidden rounded-lg border border-line bg-pressed">
        {/* Selection grammar (#361): the selected chip reads wash + blue
            emphasis + an inset ring — never the done checkbox's green
            completion fill (one meaning per fill channel, #360); the
            ring is the non-colour cue alongside aria-pressed, inset
            because the group's overflow-hidden would clip an outset
            ring on the edge chips. */}
        {chips.map((c) => (
          <button
            key={c.id}
            type="button"
            data-testid={`filter-${c.id}`}
            aria-pressed={filter === c.id}
            onClick={() => {
              setFilter(c.id);
              setConfirming(false);
            }}
            className={`px-3 py-1 text-sm ${
              filter === c.id
                ? "bg-wash text-blue font-semibold ring-1 ring-inset ring-blue"
                : "text-muted hover:text-ink"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>
      {canEdit && doneCount > 0 && !confirming && (
        <button
          type="button"
          data-testid="clear-completed"
          onClick={() => setConfirming(true)}
          className="ml-auto text-sm text-muted underline hover:text-ink"
        >
          Clear completed ({doneCount})
        </button>
      )}
      {canEdit && confirming && (
        <span className="ml-auto flex items-center gap-2 text-sm">
          <span className="text-muted">
            Delete {doneCount} completed {doneCount === 1 ? "item" : "items"}?
          </span>
          <button
            type="button"
            data-testid="clear-completed-confirm"
            onClick={() => {
              setConfirming(false);
              void clearCompleted({ listId }).catch(onError);
            }}
            className="font-medium text-danger underline hover:text-danger-pressed"
          >
            Delete
          </button>
          <button
            type="button"
            data-testid="clear-completed-cancel"
            onClick={() => setConfirming(false)}
            className="text-muted underline"
          >
            Cancel
          </button>
        </span>
      )}
    </div>
  );
}

type Item = {
  _id: Id<"items">;
  title: string;
  done: boolean;
  orderKey: string;
  assigneeMemberId?: Id<"listMembers">;
  lastEditorMemberId?: Id<"listMembers">;
  notes?: Array<Record<string, unknown>>;
  dueAt?: number;
  // The CALLER's own star stamp (#247's wire, #248's surface): present
  // exactly while THIS viewer starred the Item — private per Member,
  // so the row's star (pin, badge, bold) is this viewer's view only.
  starredAt?: number;
};

type Member = {
  _id: Id<"listMembers">;
  email: string | null;
  name: string | null;
  role: string;
  color?: string | null;
  removedAt?: number;
};

function AddItem({
  listId,
  onError,
  onTyping,
  onStopTyping,
}: {
  listId: Id<"lists">;
  onError: (err: unknown) => void;
  /** A real keystroke in the composer (#386): the typing signal. */
  onTyping: () => void;
  /** The composer's stop seams (#386): submit and unmount clear. */
  onStopTyping: () => void;
}) {
  // Optimistic (ticket #12): the item appears instantly in the editing
  // client; if the server rejects, Convex rolls the query back and the
  // caller's onError shows why.
  const add = useMutation(api.items.add).withOptimisticUpdate(
    (localStore, { listId, title }) => {
      const existing = localStore.getQuery(api.items.listItems, { listId });
      if (!existing) return;
      const trimmed = title.trim();
      const last = existing.open.reduce<string | null>(
        (acc, r) => (acc === null || r.orderKey > acc ? r.orderKey : acc),
        null,
      );
      const optimistic: typeof existing.open[number] = {
        _id: `tmp-add-${tmpCounter++}` as Id<"items">,
        _creationTime: Date.now() / 1000,
        title: trimmed,
        done: false,
        orderKey: orderKeyAfter(last),
        listId,
      };
      localStore.setQuery(api.items.listItems, { listId }, {
        open: [...existing.open, optimistic],
        done: existing.done,
      });
    },
  );
  const [title, setTitle] = useState("");
  return (
    <form
      className="mt-6 flex gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        const trimmed = title.trim();
        // Empty and too-long get the server's exact copy instantly, from
        // the same shared maxima the server enforces (#107/#110).
        if (!trimmed) {
          onError(new Error("Item title can't be empty"));
          return;
        }
        if (trimmed.length > ITEM_TITLE_MAX) {
          onError(new Error(`Item title too long (max ${ITEM_TITLE_MAX})`));
          return;
        }
        // Clear synchronously: waiting for the server to confirm would
        // race the next keystrokes and wipe them (setTitle("") landing
        // after the user already typed the next title).
        onStopTyping();
        setTitle("");
        void add({ listId, title: capitaliseFirstLetter(trimmed) }).catch(
          onError,
        );
      }}
    >
      <input
        data-testid="new-item-title"
        value={title}
        onChange={(e) => {
          setTitle(e.target.value);
          onTyping();
        }}
        placeholder="Add an item…"
        autoCapitalize="sentences"
        spellCheck
        className="flex-1 rounded-lg border border-line bg-page px-3 py-2 font-serif text-[17px] leading-[26px] placeholder:text-faint shadow-sm focus:border-blue focus:outline-none"
      />
      <button
        type="submit"
        data-testid="add-item"
        className="rounded-lg bg-blue-fill px-4 py-2 text-sm font-medium text-on-blue shadow-sm hover:bg-blue-fill-pressed"
      >
        Add
      </button>
    </form>
  );
}

let tmpCounter = 0;

// memberLabel and orderKeyAfter moved to @todotogether/shared (ticket #22)
// — the mobile item screen consumes the same label and optimistic-key logic.

function OpenItems({
  items,
  members,
  canEdit,
  viewerMemberId,
  onError,
  typists,
  adders,
  onTypingDraft,
  onStopTyping,
  onExportItem,
}: {
  items: Item[];
  members: Member[];
  canEdit: boolean;
  viewerMemberId?: Id<"listMembers">;
  onError: (err: unknown) => void;
  /** Live typing presence (#386): the paid arm's rows + ghosts. */
  typists: PresencePayloadRow[];
  adders: PresencePayloadRow[];
  onTypingDraft: (itemId: Id<"items">) => void;
  onStopTyping: () => void;
  /** #375: this row's Export to calendar tap — the page funnels it. */
  onExportItem: (itemId: Id<"items">) => void;
}) {
  return (
    <section className="mt-6">
      {items.length === 0 && adders.length === 0 ? (
        <p
          className="rounded-xl border border-dashed border-line bg-page p-6 text-center text-sm text-muted"
          data-testid="empty-items"
        >
          Nothing here yet.
        </p>
      ) : (
        <ul className="divide-y divide-line rounded-xl border border-line bg-page shadow-sm">
          {items.map((item, i) => (
            <ItemRow
              key={item._id}
              item={item}
              members={members}
              canEdit={canEdit}
              viewerMemberId={viewerMemberId}
              onError={onError}
              typingChips={typistsOnItem(typists, String(item._id))}
              onTypingDraft={onTypingDraft}
              onStopTyping={onStopTyping}
              onExportItem={onExportItem}
              prevOrderKey={i > 0 ? items[i - 1]!.orderKey : undefined}
              nextOrderKey={
                i < items.length - 1 ? items[i + 1]!.orderKey : undefined
              }
              adjacentPending={
                (i > 0 && String(items[i - 1]!._id).startsWith("tmp-")) ||
                (i < items.length - 1 &&
                  String(items[i + 1]!._id).startsWith("tmp-"))
              }
            />
          ))}
          {/* The Add-composer ghost rows (#386 = #385's ghosts): one
              per Member typing at the list end, dashed — the
              provisional spelling, the file's empty-state vocabulary
              riding the theme tokens so both desks get it (#537) —
              not tappable, replaced by the real row when the item
              lands. Paid viewers only, by construction: the free
              payload carries no typists. */}
          {adders.map((adder) => (
            <li
              key={`typing-ghost-${adder.userId}`}
              data-testid={`typing-ghost-${adder.userId}`}
              className="flex items-center gap-2 rounded-lg border border-dashed border-line bg-page px-4 py-3 text-sm italic text-muted presence-enter"
            >
              <span
                aria-hidden="true"
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: adder.color ?? "var(--tt-faint)" }}
              />
              {presenceAddingLabel(adder.name)}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function DoneItems({
  items,
  members,
  canEdit,
  viewerMemberId,
  onError,
  typists,
  onTypingDraft,
  onStopTyping,
  onExportItem,
}: {
  items: Item[];
  members: Member[];
  canEdit: boolean;
  viewerMemberId?: Id<"listMembers">;
  onError: (err: unknown) => void;
  typists: PresencePayloadRow[];
  onTypingDraft: (itemId: Id<"items">) => void;
  onStopTyping: () => void;
  /** #375: this row's Export to calendar tap — the page funnels it. */
  onExportItem: (itemId: Id<"items">) => void;
}) {
  if (items.length === 0) return null;
  return (
    <section className="mt-6" data-testid="done-section">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-muted">
        Done
      </h2>
      <ul className="mt-2 divide-y divide-line rounded-xl border border-line bg-page shadow-sm">
        {items.map((item) => (
          <ItemRow
            key={item._id}
            item={item}
            members={members}
            canEdit={canEdit}
            viewerMemberId={viewerMemberId}
            onError={onError}
            typingChips={typistsOnItem(typists, String(item._id))}
            onTypingDraft={onTypingDraft}
            onStopTyping={onStopTyping}
            onExportItem={onExportItem}
          />
        ))}
      </ul>
    </section>
  );
}

function ItemRow({
  item,
  members,
  canEdit,
  viewerMemberId,
  onError,
  typingChips,
  onTypingDraft,
  onStopTyping,
  onExportItem,
  prevOrderKey,
  nextOrderKey,
  adjacentPending,
}: {
  item: Item;
  members: Member[];
  canEdit: boolean;
  viewerMemberId?: Id<"listMembers">;
  onError: (err: unknown) => void;
  /** Who is typing on this row right now (#386): pill-run chips,
      non-interactive — presence is status, not a control. */
  typingChips: PresencePayloadRow[];
  onTypingDraft: (itemId: Id<"items">) => void;
  onStopTyping: () => void;
  /** #375: this row's Export to calendar tap — the page funnels it. */
  onExportItem: (itemId: Id<"items">) => void;
  prevOrderKey?: string;
  nextOrderKey?: string;
  adjacentPending?: boolean;
}) {
  const setDone = useMutation(api.items.setDone);
  const setTitle = useMutation(api.items.setTitle);
  // The signed-in viewer's display preference (#207) — every row
  // subscribes to the same tiny query and Convex dedupes the
  // subscription. Unknown (still loading) reads as the on default, so
  // rows render byte-identical to pre-#207 while it settles.
  const me = useQuery(api.users.me);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.title);
  const [notesOpen, setNotesOpen] = useState(false);

  const assignee = members.find((m) => m._id === item.assigneeMemberId);
  // Attribution (#12, re-ruled by #201): edits by OTHER Members render in
  // the editor's per-List colour; the viewer's own edits render in ink.
  // Done items keep their muted styling.
  const editor = members.find((m) => m._id === item.lastEditorMemberId);
  const colour = attributionColor({
    editorColor: editor?.color,
    done: item.done,
    viewerMemberId,
    lastEditorMemberId: item.lastEditorMemberId,
  });
  // An optimistic row carries a tmp- id the server has never seen — row
  // actions must wait for the real id to land (a beat, typically).
  const pending = String(item._id).startsWith("tmp-");
  const due = item.dueAt !== undefined ? dueInfo(item.dueAt) : null;
  const showDueDates = me?.showDueDates ?? true;

  // The item-line due voice (mobile dueToneColor): overdue danger,
  // today the accent, soon ink, later muted. The label rides a chip
  // box IDENTICAL to the assignee pill's (#234's level law: same
  // border, fill, padding and type → one shared centre), and it is
  // PAGE-filled so every tone holds AA on it (a wash fill put danger
  // at 3.9:1).
  const dueToneClass =
    due?.tone === "overdue"
      ? "text-danger"
      : due?.tone === "today"
        ? "text-blue"
        : due?.tone === "soon"
          ? "text-ink"
          : "text-muted";

  return (
    <li
      className="group relative flex flex-wrap items-start gap-3 px-4 py-3"
      data-testid={`item-${item.title}`}
      data-done={item.done ? "true" : "false"}
      data-colour={colour ?? ""}
    >
      <input
        type="checkbox"
        data-testid={`tick-${item.title}`}
        checked={item.done}
        disabled={!canEdit || pending}
        onChange={(e) =>
          void setDone({ itemId: item._id, done: e.target.checked }).catch(
            onError,
          )
        }
        className="mt-0.5 h-6 w-6 shrink-0 accent-done"
      />
      {editing ? (
        <form
          className="flex min-w-0 flex-1 items-center gap-2"
          onSubmit={async (e) => {
            e.preventDefault();
            await setTitle({
              itemId: item._id,
              title: capitaliseFirstLetter(draft.trim()),
            }).catch(onError);
            setEditing(false);
            onStopTyping();
          }}
        >
          <input
            data-testid="item-title-input"
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              onTypingDraft(item._id);
            }}
            autoFocus
            autoCapitalize="sentences"
            spellCheck
            className="min-w-0 flex-1 rounded border border-line px-2 py-1 font-serif text-[17px] leading-[26px]"
          />
          <button
            type="submit"
            data-testid="item-title-save"
            className="rounded-md px-2 py-1.5 text-sm underline"
          >
            Save
          </button>
        </form>
      ) : (
        <>
          {/* Bold while starred (#248 = #247's law): the emphasis rides
              in the done section too — the star survives completion. */}
          <span
            className={`min-w-0 flex-1 break-words font-serif text-[17px] leading-[26px] ${item.done ? "text-muted line-through" : "text-ink"} ${item.starredAt !== undefined ? "font-bold" : ""}`}
            style={colour ? { color: colour } : undefined}
            onDoubleClick={() => canEdit && !pending && setEditing(true)}
            title={canEdit ? "Double-click to edit" : undefined}
          >
            {item.title}
          </span>
          {/* The star badge leads the pill row (#248 = #247's law: it
              marks the row, the other pills describe it): a FILLED glyph
              — the Lists star pill's own path, one star, three
              renderers — in the due badge's box family, so the shared
              centre holds (#234) with the third badge in the line. The
              16px glyph sets the same 16px content box the badge's
              text-xs line height gives its neighbours. Muted on a done
              row (mobile StarPill's law); the caller's own view only. */}
          {item.starredAt !== undefined && (
            <span
              className="inline-flex shrink-0 items-center rounded-full border border-line bg-page px-2 py-0.5"
              data-testid={`star-pill-${item.title}`}
            >
              <svg
                viewBox="0 0 24 24"
                aria-hidden="true"
                className={item.done ? "h-4 w-4 fill-muted" : "h-4 w-4 fill-blue"}
              >
                <path d="M12 3.6l2.5 5.1 5.6.8-4 4 .9 5.6-5-2.7-5 2.7.9-5.6-4-4 5.6-.8L12 3.6Z" />
              </svg>
            </span>
          )}
          {/* Hidden, not removed, when the viewer's display preference
              (#207) is off: the date editor below keeps working. */}
          {due && showDueDates && (
            <span
              className={`shrink-0 rounded-full border border-line bg-page px-2 py-0.5 text-xs font-medium ${dueToneClass}`}
              data-testid={`due-badge-${item.title}`}
              data-tone={due.tone}
            >
              {due.label}
            </span>
          )}
          {assignee && (
            <span
              // No shrink-0, and a viewport-aware cap: at phone width a
              // fixed-max chip leaves the row menu short of the line (it
              // wraps — dragging its panel out of the viewport), so the
              // cap yields to 40% of the row first. Truncate keeps the
              // label clipped as it yields.
              className="max-w-[min(9rem,40%)] truncate rounded-full border border-line bg-page px-2 py-0.5 text-xs font-medium text-muted"
              data-testid={`assignee-of-${item.title}`}
              title={memberLabel(assignee) ?? undefined}
            >
              {memberLabel(assignee)}
            </span>
          )}
          {/* Who is typing on this row right now (#386 = #385's chips):
              the metadata line's last residents, pill-box metrics
              identical to the due/assignee chips (#234's level law —
              the line's height never changes when one appears), the
              member-colour dot leading and the name in theme tokens.
              NON-interactive: no cursor, no hover affordance. */}
          {typingChips.map((typist) => (
            <span
              key={typist.userId}
              data-testid={`typing-chip-${typist.userId}`}
              className="flex shrink-0 items-center gap-1.5 rounded-full border border-line bg-page px-2 py-0.5 text-xs font-medium text-muted presence-enter"
            >
              <span
                aria-hidden="true"
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: typist.color ?? "var(--tt-faint)" }}
              />
              {presenceTypingLabel(typist.name)}
            </span>
          ))}
          {!pending && (
            <RowMenu
              item={item}
              members={members}
              canEdit={canEdit}
              onError={onError}
              prevOrderKey={prevOrderKey}
              nextOrderKey={nextOrderKey}
              adjacentPending={adjacentPending}
              notesOpen={notesOpen}
              startEdit={() => {
                setDraft(item.title);
                setEditing(true);
              }}
              toggleNotes={() => {
                setNotesOpen((o) => !o);
                // Closing the notes panel ends its typing surface
                // (#387): the row's presence clears immediately
                // instead of riding the window out.
                onStopTyping();
              }}
              onExport={() => onExportItem(item._id)}
            />
          )}
          <NotesPanel
            item={item}
            canEdit={canEdit}
            open={notesOpen}
            onTyping={() => onTypingDraft(item._id)}
          />
        </>
      )}
    </li>
  );
}

/**
 * The per-row affordance (ticket #195): one ⋯ trigger holding every
 * editing control — rename, note, assign, due date, reorder, delete —
 * so the default row stays quiet. Pointer devices reveal the trigger on
 * row hover or keyboard focus; touch (hover: none) keeps it always on
 * screen, since there is no hover to reveal it with. A plain popover,
 * not an ARIA menu: the panel contains form controls, which the menu
 * pattern forbids. Escape and any pointer-down outside close it.
 */
function RowMenu({
  item,
  members,
  canEdit,
  onError,
  prevOrderKey,
  nextOrderKey,
  adjacentPending,
  notesOpen,
  startEdit,
  toggleNotes,
  onExport,
}: {
  item: Item;
  members: Member[];
  canEdit: boolean;
  onError: (err: unknown) => void;
  prevOrderKey?: string;
  nextOrderKey?: string;
  adjacentPending?: boolean;
  notesOpen: boolean;
  startEdit: () => void;
  toggleNotes: () => void;
  /** #375: this row's Export to calendar tap — the page funnels it. */
  onExport: () => void;
}) {
  const remove = useMutation(api.items.remove);
  const move = useMutation(api.items.move);
  const setDueDate = useMutation(api.items.setDueDate);
  // The star is the viewer's private pin (#248): the seam is #247's
  // items:setStarred, gated on membership server-side at ANY role.
  const setStarred = useMutation(api.items.setStarred);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (
        rootRef.current !== null &&
        !rootRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        ref={triggerRef}
        type="button"
        data-testid={`row-menu-${item.title}`}
        aria-label={`Actions for ${item.title}`}
        // "dialog", not "menu": this is deliberately a plain popover (it
        // holds form controls, which the menu pattern forbids) and the
        // aria must not announce it as a menu.
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={`flex h-6 w-6 items-center justify-center rounded-md text-lg leading-none text-muted transition-opacity hover:bg-pressed hover:text-muted focus-visible:opacity-100 [@media(hover:none)]:opacity-100 ${
          open
            ? "opacity-100"
            : "opacity-0 group-hover:opacity-100 group-focus-within:opacity-100"
        }`}
      >
        ⋯
      </button>
      {open && (
        <div
          data-testid={`row-menu-panel-${item.title}`}
          className="absolute right-0 top-7 z-20 w-64 rounded-xl border border-line bg-page p-1.5 shadow-lg"
        >
          {canEdit && (
            <label className="block px-1.5 py-1 text-xs text-muted">
              Assign to
              <AssigneePicker
                itemId={item._id}
                item={item}
                members={members}
                onError={onError}
              />
            </label>
          )}
          {canEdit && (
            <label className="block px-1.5 py-1 text-xs text-muted">
              Due date
              <input
                type="date"
                aria-label={`Due date of ${item.title}`}
                data-testid={`due-date-${item.title}`}
                value={item.dueAt !== undefined ? dueMsToDate(item.dueAt) : ""}
                onChange={(e) => {
                  const value = e.target.value;
                  // Only EMPTY clears; an unparseable non-empty value
                  // (not producible by type=date today) must not
                  // silently wipe an existing date.
                  const ms =
                    value === "" ? undefined : dueDateToMs(value);
                  if (ms === null) return;
                  void setDueDate({
                    itemId: item._id,
                    dueAt: ms,
                  }).catch(onError);
                }}
                className="mt-1 w-full rounded border border-line bg-page px-2 py-1 text-xs"
              />
            </label>
          )}
          {canEdit && (
            <MenuRow
              testid={`edit-${item.title}`}
              onClick={() => {
                setOpen(false);
                startEdit();
              }}
            >
              Edit text
            </MenuRow>
          )}
          <MenuRow
            testid={`notes-open-${item.title}`}
            onClick={() => {
              setOpen(false);
              toggleNotes();
            }}
          >
            {notesOpen ? "Close details" : "Add details"}
          </MenuRow>
          <MenuRow
            testid={`export-item-${item.title}`}
            onClick={() => {
              setOpen(false);
              onExport();
            }}
          >
            Export to calendar
          </MenuRow>
          {/* The star is the viewer's own pin (#248 = #247's law):
              EVERY role sees this row, Viewer included — it is not List
              content, the same rule as the Lists screen's Star/Unstar
              (#70). The tap flips the caller's own stamp. */}
          <MenuRow
            testid={
              item.starredAt !== undefined
                ? `unstar-item-${item.title}`
                : `star-item-${item.title}`
            }
            onClick={() => {
              setOpen(false);
              void setStarred({
                itemId: item._id,
                starred: item.starredAt === undefined,
              }).catch(onError);
            }}
          >
            {item.starredAt !== undefined ? "Unstar" : "Star"}
          </MenuRow>
          {canEdit && prevOrderKey !== undefined && !adjacentPending && (
            // Moves are the only rows gated on the optimistic neighbour:
            // its orderKey is still changing; every other action here
            // addresses THIS row's real id.
            <MenuRow
              testid={`move-up-${item.title}`}
              onClick={() => {
                setOpen(false);
                void move({
                  itemId: item._id,
                  beforeOrderKey: undefined,
                  afterOrderKey: prevOrderKey,
                }).catch(onError);
              }}
            >
              Move up
            </MenuRow>
          )}
          {canEdit && nextOrderKey !== undefined && !adjacentPending && (
            <MenuRow
              testid={`move-down-${item.title}`}
              onClick={() => {
                setOpen(false);
                void move({
                  itemId: item._id,
                  beforeOrderKey: nextOrderKey,
                  afterOrderKey: undefined,
                }).catch(onError);
              }}
            >
              Move down
            </MenuRow>
          )}
          {canEdit && (
            <MenuRow
              danger
              testid={`remove-${item.title}`}
              onClick={() => {
                setOpen(false);
                void remove({ itemId: item._id }).catch(onError);
              }}
            >
              Delete
            </MenuRow>
          )}
        </div>
      )}
    </div>
  );
}

function MenuRow({
  testid,
  onClick,
  children,
  danger = false,
}: {
  testid: string;
  onClick: () => void;
  children: ReactNode;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`mt-0.5 flex w-full items-center rounded-md px-2 py-1.5 text-left text-sm first:mt-0 ${
        danger
          ? "text-danger hover:bg-danger hover:text-on-blue"
          : "text-muted hover:bg-pressed"
      }`}
    >
      {children}
    </button>
  );
}

/**
 * Notes panel (ticket #13, ADR-0008): lazy-loaded BlockNote editor on the
 * Item's Notes field. Any Editor can write; Viewers read-only (mirroring
 * the server's setNotes role gate). Saves are debounced 600ms and the
 * editor stays mounted while saving, so typing is never blocked.
 * Opened from the row menu (#195), which owns the open state.
 */
function NotesPanel({
  item,
  canEdit,
  open,
  onTyping,
}: {
  item: Item;
  canEdit: boolean;
  open: boolean;
  /** #387: the notes-editor host's typing signal — rides the same
   * throttled broadcaster and the same `item:` surface as the row's
   * rename, so rename-typing and notes-typing are one presence row. */
  onTyping: () => void;
}) {
  const setNotes = useMutation(api.items.setNotes);
  const [error, setError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved">(
    "idle",
  );
  const [timer, setTimer] = useState<ReturnType<typeof setTimeout> | null>(
    null,
  );

  function scheduleSave(doc: NotesDoc) {
    onTyping();
    if (timer) clearTimeout(timer);
    setSaveState("saving");
    const t = setTimeout(async () => {
      try {
        await setNotes({ itemId: item._id, notes: doc as never[] });
        setSaveState("saved");
      } catch (err) {
        setError(errorMessage(err, GENERIC_ERROR_MESSAGE));
        setSaveState("idle");
      }
    }, 600);
    setTimer(t);
  }

  if (!open) return null;

  return (
    <div className="order-last min-w-full">
      {error && (
        <p className="text-sm text-danger" data-testid="notes-error">
          {error}
        </p>
      )}
      {/* min-h reserves the indicator's line (#372): Saving…/Saved
          appearing must never shift the editor down. */}
      <div className="flex min-h-5 items-center gap-2">
        {saveState === "saving" && (
          <span className="text-xs text-muted" data-testid="notes-saving">
            Saving…
          </span>
        )}
        {saveState === "saved" && (
          <span className="text-xs text-muted" data-testid="notes-saved">
            Saved
          </span>
        )}
      </div>
      <Suspense
        fallback={
          <p className="text-sm text-muted" data-testid="notes-loading">
            Loading editor…
          </p>
        }
      >
        <NotesEditor
          testIdPrefix={`item-${item._id}`}
          itemId={item._id}
          initialNotes={item.notes as NotesDoc | undefined}
          readOnly={!canEdit}
          onChange={canEdit ? scheduleSave : undefined}
        />
      </Suspense>
    </div>
  );
}

function AssigneePicker({
  itemId,
  item,
  members,
  onError,
}: {
  itemId: Id<"items">;
  item: Item;
  members: Member[];
  onError: (err: unknown) => void;
}) {
  const setAssignee = useMutation(api.items.setAssignee);
  // Removed Members stay resolvable as history on existing Assignments
  // but can't take new ones (#15).
  const assignable = members.filter((m) => m.removedAt === undefined);
  return (
    <select
      data-testid={`assignee-picker-${item.title}`}
      value={item.assigneeMemberId ?? ""}
      onChange={(e) =>
        void setAssignee({
          itemId,
          assigneeMemberId: e.target.value ? (e.target.value as Id<"listMembers">) : undefined,
        }).catch(onError)
      }
      className="mt-1 w-full rounded border border-line bg-page px-2 py-1 text-xs"
    >
      <option value="">—</option>
      {assignable.map((m) => (
        <option key={m._id} value={m._id}>
          {memberLabel(m)}
        </option>
      ))}
    </select>
  );
}

/**
 * Share panel (ticket #14, Owner/Admin only): the List's one active
 * Invite Link with a Role toggle. The toggle is the live link's truth
 * (selectedInviteRole, #400) — a fresh mount shows what the current
 * link grants, never a Viewer default — and a tap records a choice tied
 * to that link, so regenerating without a deliberate switch preserves
 * the link's role while a regenerated link re-syncs the display.
 * Regenerating still replaces the link server-side — the old URL dies
 * the moment the new one appears.
 */
function SharePanel({ listId }: { listId: Id<"lists"> }) {
  const invite = useQuery(api.invites.getForList, { listId });
  const generate = useMutation(api.invites.generate);
  const [choice, setChoice] = useState<InviteRoleChoice | null>(null);
  const role = selectedInviteRole(invite, choice);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef<number | null>(null);

  // The copied confirmation is transient (#198): it clears itself after
  // a beat, and re-copying cancels the pending reset so rapid copies
  // never flicker the label off mid-visit. The panel unmounts with the
  // list — drop any in-flight timer with it.
  useEffect(
    () => () => {
      if (copiedTimer.current !== null)
        window.clearTimeout(copiedTimer.current);
    },
    [],
  );

  async function makeLink() {
    setError(null);
    setBusy(true);
    try {
      await generate({ listId, role });
    } catch (err) {
      setError(errorMessage(err, GENERIC_ERROR_MESSAGE));
    } finally {
      setBusy(false);
    }
  }

  function copyLink(url: string) {
    // Feedback only claims what succeeded: a rejected clipboard write
    // (permission denied, insecure context) leaves the label unshown,
    // silenced deliberately — the #204 best-effort posture.
    void navigator.clipboard
      ?.writeText(url)
      .then(() => {
        setCopied(true);
        if (copiedTimer.current !== null)
          window.clearTimeout(copiedTimer.current);
        copiedTimer.current = window.setTimeout(() => setCopied(false), 2_000);
      })
      .catch(() => undefined);
  }

  const linkUrl = invite ? `${window.location.origin}/invite/${invite.token}` : null;

  return (
    <section
      className="mt-8 rounded-xl border border-line bg-page p-4 shadow-sm"
      data-testid="share-panel"
    >
      <h2 className="text-sm font-semibold uppercase tracking-wide text-muted">
        Share
      </h2>
      {error && (
        <p className="mt-2 text-sm text-danger" data-testid="share-error">
          {error}
        </p>
      )}
      {invite === undefined ? null : (
        <div className="mt-3">
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-1 text-sm text-muted">
              <input
                type="radio"
                name="invite-role"
                checked={role === "viewer"}
                onChange={() =>
                  setChoice({ linkToken: invite?.token ?? null, role: "viewer" })
                }
                data-testid="invite-role-viewer"
              />
              Viewer
            </label>
            <label className="flex items-center gap-1 text-sm text-muted">
              <input
                type="radio"
                name="invite-role"
                checked={role === "editor"}
                onChange={() =>
                  setChoice({ linkToken: invite?.token ?? null, role: "editor" })
                }
                data-testid="invite-role-editor"
              />
              Editor
            </label>
            <button
              type="button"
              onClick={() => void makeLink()}
              disabled={busy}
              className="ml-auto rounded-lg bg-blue-fill px-3 py-1.5 text-sm font-medium text-on-blue shadow-sm hover:bg-blue-fill-pressed disabled:opacity-60"
              data-testid={invite ? "regenerate-invite" : "generate-invite"}
            >
              {invite ? "Regenerate link" : "Create invite link"}
            </button>
          </div>
          {linkUrl && (
            <div className="mt-3">
              <input
                readOnly
                value={linkUrl}
                data-testid="invite-link"
                onFocus={(e) => e.target.select()}
                className="w-full rounded border border-line bg-paper px-2 py-1.5 font-mono text-xs text-muted"
              />
              <button
                type="button"
                data-testid="copy-invite"
                onClick={() => copyLink(linkUrl)}
                className="mt-1 text-xs text-muted underline"
              >
                Copy link
              </button>
              {copied && (
                <span
                  className="ml-2 text-xs text-blue"
                  data-testid="copy-invite-copied"
                  role="status"
                >
                  Copied
                </span>
              )}
              {invite?.role === "editor" && (
                <p className="mt-2 text-xs text-warn" data-testid="editor-link-warning">
                  Anyone with this link can edit this list.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

/**
 * Members screen (tickets #14/#15): everyone on the List with their Role
 * and per-List colour swatch — the same colour their edits are attributed
 * in. Role selects + Kick buttons reflect capability derived through the
 * SHARED matrix (canChangeMemberRole / roleOptionsFor — the one home
 * mobile's member role sheet already consumes, #659; the #399 law that a
 * role string outside the vocabulary manages nothing rides along) —
 * every rule is ALSO enforced server-side (convex/members.ts); the UI
 * merely hides what you can't do. Leave is the one control every
 * non-owner Member gets.
 */
function MembersPanel({
  listId,
  members,
  callerRole,
  myMemberId,
}: {
  listId: Id<"lists">;
  members: Member[];
  callerRole: string;
  myMemberId: Id<"listMembers">;
}) {
  const setRole = useMutation(api.members.setRole);
  const kick = useMutation(api.members.kick);
  const leave = useMutation(api.members.leave);
  const [error, setError] = useState<string | null>(null);

  const isOwner = callerRole === "owner";

  // Removed Members (kicked/left) don't render — their Assignments keep
  // resolving via the full members list elsewhere (#15).
  const active = members.filter((m) => m.removedAt === undefined);

  // Capability derives ONCE per row through the shared matrix (#659):
  // the caller's role, the target's role, and self-ness — the same
  // call shape mobile's panel makes. Roles arrive as raw wire strings,
  // so both sides cast at the boundary; the shared door refuses a
  // non-role string instead of trusting the cast (#399: a corrupt row
  // manages nothing).
  const canManage = (target: Member): boolean =>
    canChangeMemberRole(
      callerRole as Role,
      target.role as Role,
      target._id === myMemberId,
    );

  // The per-caller role sheet's offering (only the Owner grants Admin —
  // the shared derivation's rule, not a local one).
  const roleOptions = () => roleOptionsFor(callerRole as Role);

  async function run(action: () => Promise<unknown>) {
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(errorMessage(err, GENERIC_ERROR_MESSAGE));
    }
  }

  return (
    <section
      className="mt-4 rounded-xl border border-line bg-page p-4 shadow-sm"
      data-testid="members-panel"
    >
      <h2 className="text-sm font-semibold uppercase tracking-wide text-muted">
        Members
      </h2>
      {error && (
        <p className="mt-2 text-sm text-danger" data-testid="members-error">
          {error}
        </p>
      )}
      <ul className="mt-2 space-y-1.5">
        {active.map((m) => {
          const label = m.email ?? m.role;
          const manageable = canManage(m);
          return (
            <li
              key={m._id}
              className="flex items-center gap-2 text-sm"
              data-testid={`member-row-${label}`}
              data-member-id={m._id}
            >
              <span
                className="inline-block h-3 w-3 shrink-0 rounded-full"
                // No colour yet: the hairline is the quietest placeholder.
                style={{ backgroundColor: m.color ?? "var(--tt-line)" }}
                data-testid={`member-colour-${label}`}
                data-colour={m.color ?? ""}
              />
              <span className="text-ink">{memberLabel(m)}</span>
              {manageable && (
                <select
                  data-testid={`role-select-${label}`}
                  value={m.role}
                  onChange={(e) =>
                    void run(() =>
                      setRole({
                        listId,
                        memberId: m._id,
                        role: e.target.value as "viewer" | "editor" | "admin",
                      }),
                    )
                  }
                  className="ml-auto rounded border border-line bg-page px-1 py-0.5 text-xs capitalize"
                >
                  {roleOptions().map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              )}
              <span
                className={`capitalize text-muted ${manageable ? "" : "ml-auto"}`}
                data-testid={`member-role-${label}`}
              >
                {m.role}
              </span>
              {manageable && (
                <button
                  type="button"
                  data-testid={`kick-${label}`}
                  onClick={() =>
                    void run(() => kick({ listId, memberId: m._id }))
                  }
                  className="text-danger hover:underline"
                >
                  Kick
                </button>
              )}
            </li>
          );
        })}
      </ul>
      {!isOwner && (
        <button
          type="button"
          data-testid="leave-list"
          onClick={() => void run(() => leave({ listId }))}
          className="mt-3 text-sm text-danger hover:underline"
        >
          Leave this list
        </button>
      )}
    </section>
  );
}
